import { useEffect, useState } from 'react';
import {
  fallbackAspectFor,
  getProbeUrl,
  probeImageRatio,
  readDeclaredAspect,
  orientationFor,
  TVideoAspect,
} from '@/modules/content/common/videoOrientation';
import { TContent } from '@/types/Content.types';

// Resolve a video's shape, preferring a declared value and falling back to a poster probe.
export const useVideoAspect = (content: TContent): TVideoAspect => {
  const declared = readDeclaredAspect(content);
  const probeUrl = declared ? null : getProbeUrl(content);

  const [probed, setProbed] = useState<TVideoAspect | null>(null);

  useEffect(() => {
    if (!probeUrl) {
      setProbed(null);
      return;
    }

    let isCurrent = true;
    setProbed(null);

    probeImageRatio(probeUrl).then((ratio) => {
      if (!isCurrent) return;

      setProbed(
        ratio
          ? { orientation: orientationFor(ratio), ratio, isConfident: true, isResolving: false }
          : { ...fallbackAspectFor(content), isResolving: false },
      );
    });

    return () => {
      isCurrent = false;
    };
    // Keyed on the probe URL: the same video should not re-probe when unrelated fields change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [probeUrl]);

  if (declared) return declared;
  if (probed) return probed;

  // Nothing to probe — commit to the default immediately rather than showing a delay
  // state that would never resolve.
  if (!probeUrl) return fallbackAspectFor(content);

  return { ...fallbackAspectFor(content), isResolving: true };
};

export default useVideoAspect;
